<template>
    <section
        class="slice slice-body slice-trends-on-demand-skills"
        :class="{'slice-trends-on-demand-skills--grey': backgroundColor === 'grey'}"
        data-testid="slice-trends-on-demand-skills"
    >
        <div class="slice-content">
            <TitleBrand :field="slice.primary.title" />
            <div class="slice-trends-on-demand-skills__content">
                <div class="slice-trends-on-demand-skills__image">
                    <PrismicImage :field="slice.primary.image" :alt="slice.primary.image.alt" />
                </div>
                <div>
                    <div class="slice-trends-on-demand-skills__highlights">
                        <div
                            v-for="(highlight, index) in slice.primary.highlights"
                            :key="index"
                            class="slice-trends-on-demand-skills__highlight"
                            :data-testid="`slice-trends-on-demand-skills-highlight-${index}`"
                        >
                            <div>{{ highlight.title }}</div>
                            <div>{{ highlight.subtitle }}</div>
                        </div>
                    </div>
                    <div class="joy-mb-8">
                        <h3>{{ slice.primary.demandSkillsTitle }}</h3>
                        <TrendsTagList :tags="demandSkills" />
                    </div>
                    <div class="joy-mb-8">
                        <h3>{{ slice.primary.supplySkillsTitle }}</h3>
                        <TrendsTagList :tags="supplySkills" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled, type Content, type LinkField} from '@prismicio/client';
    import {getSliceComponentProps, PrismicImage} from '@prismicio/vue';
    import TitleBrand from '~/components/TitleBrand.vue';
    import {type TrendTagItem} from '~/components/trends/TrendsTag.vue';
    import TrendsTagList from '~/components/trends/TrendsTagList.vue';

    const {slice} = defineProps(getSliceComponentProps<Content.TrendsOnDemandSkillsSlice>(['slice', 'index', 'slices', 'context']));

    function linkFieldToTrendTag(link: LinkField): TrendTagItem | null {
        if (!link.text) return null;

        const isLinkFilled = isFilled.link(link);
        return {text: link.text, href: isLinkFilled ? link.url : undefined};
    }

    function mapRepeatableLinksToTrendTags(links: readonly LinkField[]): TrendTagItem[] {
        return links.map(linkFieldToTrendTag).filter((t): t is TrendTagItem => t !== null);
    }

    const demandSkills = computed(() => mapRepeatableLinksToTrendTags(slice.primary.demandSkills));
    const supplySkills = computed(() => mapRepeatableLinksToTrendTags(slice.primary.supplySkills));
    const backgroundColor = computed(() => slice.primary.backgroundColor);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-trends-on-demand-skills {
        &--grey {
            background-color: var(--joy-color-neutral-20);
        }

        &__content {
            @include mq.screen_md(min) {
                display: flex;
                align-items: center;
                gap: var(--joy-core-spacing-7);
            }

            @include mq.screen_lg(min) {
                justify-content: center;
                gap: 98px;
            }
        }

        &__image {
            text-align: center;

            img {
                height: auto;
                max-height: 172px;

                @include mq.screen_md(min) {
                    min-height: 355px;
                    width: 100%;
                    object-fit: contain;
                }
            }
        }

        &__highlights {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--joy-core-spacing-2);
            color: var(--joy-color-neutral-0);
            margin-top: var(--joy-core-spacing-9);
            margin-bottom: var(--joy-core-spacing-9);
            text-align: center;

            @include mq.screen_sm(min) {
                grid-template-columns: 1fr 1fr 1fr;
                column-gap: var(--joy-core-spacing-5);
            }

            @include mq.screen_md(min) {
                margin-top: 0;
            }
        }

        &__highlight {
            background-color: var(--joy-color-primary-50);
            padding: var(--joy-core-spacing-4);
            border-radius: var(--joy-core-radius-4);

            &:first-child {
                grid-column: span 2;

                @include mq.screen_sm(min) {
                    grid-column: initial;
                }
            }

            @include mq.screen_md(min) {
                max-width: 225px;
            }

            div:last-child,
            div:first-child {
                font-family: var(--joy-font-family-title);
            }

            div:first-child {
                font-size: var(--joy-font-size-primary-800);
                line-height: var(--joy-line-height-large);
                white-space: nowrap;

                @include mq.screen_sm(min) {
                    font-size: var(--joy-font-size-secondary-800);
                }
            }
        }

        h3 {
            font-size: var(--joy-font-size-primary-400);
            margin-bottom: var(--joy-core-spacing-4);

            @include mq.screen_sm(min) {
                font-size: var(--joy-font-size-primary-800);
            }
        }

        &__skills {
            display: flex;
            flex-wrap: wrap;
            gap: var(--joy-core-spacing-3);

            &:not(:last-child) {
                margin-bottom: var(--joy-core-spacing-9);
            }
        }

        &__skill {
            cursor: pointer;
            padding: var(--joy-core-spacing-2) var(--joy-core-spacing-3);
            border-radius: var(--joy-core-radius-3);
            background-color: var(--joy-color-brand-primary-10);
            font-weight: var(--joy-font-weight-normal);

            a,
            a:hover,
            a:visited {
                text-decoration: none;
                color: inherit;
            }
        }
    }
</style>
